import React, { useState } from 'react';
import { Map as MapIcon, Navigation, ExternalLink, Eye } from 'lucide-react';

/*
 * SiteEmbedMap — a real Google map of one site, inside the drawer.
 *
 * ─── WHY THE EMBED API AND NOT THE MAPS JS API ───────────────────────────────
 * Google: "All Maps Embed API requests are available at no charge with
 * unlimited usage." The JS API bills per map load and needs
 * `script-src https://maps.googleapis.com` on a CSP that is deliberately
 * `script-src 'self'` — a much bigger hole than one iframe origin, because a
 * script tag from another origin can do anything the page can.
 *
 * What we give up is a SINGLE map showing every site at once; an embed shows
 * one place. The all-sites view stays the SiteMap chart, which draws our own
 * coordinates and needs no third party at all.
 *
 * ─── THE KEY IS IN THE URL, AND THAT IS THE SUPPORTED MODEL ──────────────────
 * An iframe src is in the DOM, so an Embed key is public by construction.
 * Google's answer is an HTTP-referrer restriction, and the key used here MUST
 * be a separate one from GOOGLE_PLACES_KEY — restricted to the Maps Embed API
 * and to tekstak.app — because GOOGLE_PLACES_KEY is server-only and spends
 * money per request. Putting that one in a bundle would be handing out a bill.
 *
 * ─── IT PREFERS THE PLACE ID ─────────────────────────────────────────────────
 * `place_id:ChIJ…` pins the verified listing. Falling back to the address is a
 * text search, which is the same thing the 200m guard exists to distrust — so
 * when that is all we have, the panel says the pin is approximate rather than
 * letting a reader assume it was confirmed.
 */

const EMBED_KEY = process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY || '';

function embedQuery(site) {
  if (site.googlePlaceId) return `place_id:${site.googlePlaceId}`;
  const parts = [site.addressLine1, site.city, site.state, site.postalCode].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

/** Directions need no key and no embed — a plain Maps link does it. */
export function directionsUrl(site) {
  const q = [site.addressLine1, site.city, site.state, site.postalCode].filter(Boolean).join(', ');
  if (!q && !site.googlePlaceId) return null;
  const base = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q || site.siteName || '')}`;
  return site.googlePlaceId
    ? `${base}&destination_place_id=${encodeURIComponent(site.googlePlaceId)}`
    : base;
}

export default function SiteEmbedMap({ site }) {
  const [mode, setMode] = useState('place');
  const q = embedQuery(site);
  const dir = directionsUrl(site);

  if (!q) {
    return (
      <section className="sites-map-embed">
        <h5><MapIcon size={14} aria-hidden /> Map</h5>
        <p className="sites-muted">
          No address and no Google listing on this site, so there is nothing to put a pin in.
          Add an address and it will be mapped.
        </p>
      </section>
    );
  }

  if (!EMBED_KEY) {
    return (
      <section className="sites-map-embed">
        <h5><MapIcon size={14} aria-hidden /> Map</h5>
        <p className="sites-muted">
          The embedded map is off — no Maps Embed key is configured for this build.
          {dir && (
            <>
              {' '}
              <a href={dir} target="_blank" rel="noreferrer">
                Open it in Google Maps <ExternalLink size={11} aria-hidden />
              </a>
            </>
          )}
        </p>
      </section>
    );
  }

  const src = `https://www.google.com/maps/embed/v1/${mode}`
    + `?key=${encodeURIComponent(EMBED_KEY)}`
    + `&${mode === 'streetview' ? 'location' : 'q'}=${encodeURIComponent(q)}`
    + (mode === 'place' ? '&zoom=17' : '');

  return (
    <section className="sites-map-embed">
      <div className="sites-map-head">
        <h5><MapIcon size={14} aria-hidden /> Map</h5>
        <div className="sites-map-modes">
          <button
            type="button"
            className={mode === 'place' ? 'is-on' : undefined}
            onClick={() => setMode('place')}
          >
            Map
          </button>
          <button
            type="button"
            className={mode === 'streetview' ? 'is-on' : undefined}
            onClick={() => setMode('streetview')}
            title="Street View, if Google has imagery here"
          >
            <Eye size={12} aria-hidden /> Street
          </button>
          {dir && (
            <a href={dir} target="_blank" rel="noreferrer">
              <Navigation size={12} aria-hidden /> Directions
            </a>
          )}
        </div>
      </div>

      <iframe
        key={`${mode}-${q}`}
        title={`Map of ${site.siteName}`}
        className="sites-map-frame"
        src={src}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />

      {!site.googlePlaceId && (
        <p className="sites-muted sites-map-note">
          This pin is a search for the address, not a confirmed listing — check it before
          you rely on it.
        </p>
      )}
    </section>
  );
}
